/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	ForeignPlantComponent,
	MultiplayerStateComponent,
	SharedWorldComponent,
} from '../../components/MultiplayerComponents';
import { deleteEntity, getOnlyEntity } from '../../utils/entityUtils';

import { AssetDatabaseComponent } from '../../components/AssetDatabaseComponent';
import { GameStateComponent } from '../../components/GameStateComponent';
import { PlantedComponent } from '../../components/PlantingComponents';
import { SavableObject } from '../../components/SaveDataComponents';
import { System } from 'ecsy';
import { THREEGlobalComponent } from '../../components/THREEGlobalComponent';
import { loadAllPlots } from '../../lib/firebase/PlotStorage';

/**
 * Renders the shared world's other players (design Phase 2 + Phase 4).
 *
 * The world is one communal island: every player's plants live under their own
 * Firestore plot (`plots/{plotId}`), and are rendered by everyone at the real
 * coordinates they were planted.
 *
 *   - On join (once the shared world is live) it loads every plot. Other players'
 *     plants become `ForeignPlantComponent` entities: full plant visuals via the
 *     normal deserialize path, but NOT tagged SavableObject (so they never enter
 *     the local save) and skipped by harvesting.
 *   - The player's OWN plot is restored from the cloud as normal local plants
 *     (tagged SavableObject) for any plant not already present locally — this is
 *     what lets a garden follow the player to a new device / browser. Restored
 *     plants are then written back to this device's local save so localForage
 *     stays the local source of truth. De-dupe is by `plantId`, and the local
 *     (localForage) load reliably finishes long before the network auth chain, so
 *     there is no double-add of plants that were already loaded locally.
 *   - Live: it relays the local player's plant add/remove through the Colyseus room
 *     and applies peers' relays, so gardens update in real time.
 *
 * Entirely dormant unless the shared world is live (Firebase configured + signed
 * in). In single-player nothing here runs and no foreign plants exist.
 */
export class PlotWorldSystem extends System {
	init() {
		this.scene = null;
		this._plotsLoaded = false;
		this._handlersBound = false;
		// entity id -> plantId, so we can announce removals after the plant entity
		// (and its components) are already gone.
		this._localPlantIds = new Map();
	}

	execute() {
		const gameManager = getOnlyEntity(this.queries.gameManager, false);
		if (!gameManager) return;
		this.scene = gameManager.getComponent(THREEGlobalComponent).scene;

		const swEntity = getOnlyEntity(this.queries.sharedWorld, false);
		const sharedWorld = swEntity
			? swEntity.getComponent(SharedWorldComponent)
			: null;
		const live = !!(sharedWorld && sharedWorld.configured && sharedWorld.ready);

		if (live) {
			this._bindRoomHandlers();
			if (!this._plotsLoaded) {
				this._plotsLoaded = true;
				this._loadWorld(gameManager, sharedWorld);
			}
		}

		// Always drain the reactive query (so it never accumulates or leaks the id
		// map in single-player); only broadcast to peers when the world is live.
		this._processLocalPlants(sharedWorld, live);
	}

	/**
	 * Maintain the local plant id map and, when the shared world is live, relay
	 * the player's just-added / just-removed plants to peers.
	 */
	_processLocalPlants(sharedWorld, live) {
		const room = live ? this._getRoom() : null;

		this.queries.localPlants.added.forEach((entity) => {
			const planted = entity.getComponent(PlantedComponent);
			if (planted) this._localPlantIds.set(entity.id, planted.plantId);
			if (room) {
				const plant = this._serializeEntity(entity);
				room.send('plant_add', {
					plotId: sharedWorld.plotId,
					ownerUid: sharedWorld.uid,
					plant,
				});
			}
		});

		this.queries.localPlants.removed.forEach((entity) => {
			const plantId = this._localPlantIds.get(entity.id);
			this._localPlantIds.delete(entity.id);
			if (room && plantId) {
				room.send('plant_remove', {
					plotId: sharedWorld.plotId,
					plantId,
				});
			}
		});
	}

	/** Register the plant relay handlers on the room exactly once. */
	_bindRoomHandlers() {
		if (this._handlersBound) return;
		const room = this._getRoom();
		if (!room) return;
		this._handlersBound = true;

		room.onMessage('plant_add', (data) => {
			if (!data || !data.plant) return;
			this._spawnForeignPlant(data.plant, {
				plotId: data.plotId,
				ownerUid: data.ownerUid,
			});
		});

		room.onMessage('plant_remove', (data) => {
			if (!data || !data.plantId) return;
			this._removeForeignPlant(data.plantId);
		});
	}

	/**
	 * Load the whole world on join: render other players' plots read-only and
	 * restore the player's own plot from the cloud (cross-device garden restore).
	 */
	async _loadWorld(gameManager, sharedWorld) {
		try {
			const plots = await loadAllPlots();
			let neighbours = 0;
			plots.forEach((plot) => {
				if (plot.ownerUid === sharedWorld.uid) {
					this._restoreOwnPlot(gameManager, plot);
					return;
				}
				neighbours++;
				(plot.plants || []).forEach((plant) => {
					this._spawnForeignPlant(plant, {
						plotId: plot.id,
						ownerUid: plot.ownerUid,
					});
				});
			});
			console.info(`[SharedWorld] Loaded ${neighbours} neighbour plot(s).`);
		} catch (e) {
			console.error('[SharedWorld] Failed to load the shared world', e);
		}
	}

	/**
	 * Restore the player's own plot from the cloud as local, savable plants —
	 * any plant not already present locally (matched by plantId). This is what
	 * carries a garden across devices/browsers. Restored plants are then flagged
	 * for a local save so localForage stays the local source of truth.
	 */
	_restoreOwnPlot(gameManager, plot) {
		const cloudPlants = plot.plants || [];
		if (!cloudPlants.length) return;

		const localIds = new Set(
			this.queries.localPlants.results
				.map((entity) => entity.getComponent(PlantedComponent))
				.filter((planted) => planted && planted.plantId)
				.map((planted) => planted.plantId),
		);

		let restored = 0;
		cloudPlants.forEach((plant) => {
			const plantId = this._plantIdOf(plant);
			if (plantId && localIds.has(plantId)) return; // already here locally
			this._deserializePlant(plant, false, null);
			restored++;
		});

		if (restored > 0) {
			console.info(
				`[SharedWorld] Restored ${restored} of your plant(s) from the cloud.`,
			);
			// Persist the restored garden to this device so local stays authoritative.
			const gameState = gameManager.getMutableComponent(GameStateComponent);
			if (gameState) gameState.updateGardenPending = true;
		}
	}

	/**
	 * Deserialize a single serialized plant as a foreign (other-player) plant:
	 * full visuals via the normal component path, minus SavableObject, plus a
	 * ForeignPlantComponent so it is never saved and never harvested locally.
	 */
	_spawnForeignPlant(plantJson, plotMeta) {
		const plantId = this._plantIdOf(plantJson);
		if (plantId && this._foreignPlantExists(plantId)) return; // de-dupe
		this._deserializePlant(plantJson, true, plotMeta);
	}

	/**
	 * Rebuild a plant entity from its serialized form. When `foreign` is true the
	 * plant is a read-only neighbour: SavableObject is stripped and a
	 * ForeignPlantComponent is added. When false it is restored as one of the
	 * player's own plants (SavableObject kept, no ForeignPlantComponent) so it
	 * renders, harvests, and saves exactly like a locally-planted flower.
	 * @param {Object} plantJson serialized plant (`{ ComponentName: {...} }`)
	 * @param {boolean} foreign whether this is another player's plant
	 * @param {?{plotId:string, ownerUid:string}} plotMeta owner info (foreign only)
	 */
	_deserializePlant(plantJson, foreign, plotMeta) {
		const entity = this.world.createEntity();
		for (const key in plantJson) {
			if (foreign && key === SavableObject.getName()) continue; // never savable
			const ComponentType = getComponentFromName(this.world, key);
			if (!ComponentType) continue;
			entity.addComponent(ComponentType);
			const instance = entity.getMutableComponent(ComponentType);
			if (instance && instance.deserialize)
				instance.deserialize(plantJson[key]);
		}

		const assetDatabase = getOnlyEntity(
			this.queries.assetDatabase,
		).getComponent(AssetDatabaseComponent);
		const components = entity.getComponents();
		for (const key in components) {
			const component = components[key];
			if (component.afterDeserialize) {
				component.afterDeserialize(entity, this.scene, assetDatabase);
			}
		}

		if (foreign) {
			entity.addComponent(ForeignPlantComponent, {
				plantId: this._plantIdOf(plantJson),
				plotId: (plotMeta && plotMeta.plotId) || '',
				ownerUid: (plotMeta && plotMeta.ownerUid) || '',
			});
		}
		return entity;
	}

	/** Remove a foreign plant (and its collider) by plant id. */
	_removeForeignPlant(plantId) {
		const match = this.queries.foreignPlants.results.find(
			(entity) =>
				entity.getComponent(ForeignPlantComponent).plantId === plantId,
		);
		if (!match) return;
		const planted = match.getComponent(PlantedComponent);
		const tinyCollider = planted ? planted.tinyColliderEntity : null;
		if (tinyCollider && tinyCollider.alive) {
			deleteEntity(this.scene, tinyCollider);
		}
		deleteEntity(this.scene, match);
	}

	_foreignPlantExists(plantId) {
		return this.queries.foreignPlants.results.some(
			(entity) =>
				entity.getComponent(ForeignPlantComponent).plantId === plantId,
		);
	}

	_plantIdOf(plantJson) {
		const planted = plantJson[PlantedComponent.getName()];
		return planted ? planted.plantId || '' : '';
	}

	_getRoom() {
		const entity = getOnlyEntity(this.queries.multiplayer, false);
		const state = entity
			? entity.getComponent(MultiplayerStateComponent)
			: null;
		return state && state.isConnected ? state.room : null;
	}

	/** Serialize one entity into the shared save format `{ ComponentName: {...} }`. */
	_serializeEntity(entity) {
		const components = entity.getComponents();
		const obj = {};
		for (const key in components) {
			const component = components[key];
			if (!component.serialize) continue;
			obj[component.getName()] = component.serialize();
		}
		return obj;
	}
}

const getComponentFromName = (world, componentName) => {
	const component = world.componentsManager.Components.find(
		(c) => c.getName() === componentName,
	);
	return component || undefined;
};

PlotWorldSystem.queries = {
	gameManager: { components: [THREEGlobalComponent] },
	sharedWorld: { components: [SharedWorldComponent] },
	multiplayer: { components: [MultiplayerStateComponent] },
	assetDatabase: { components: [AssetDatabaseComponent] },
	foreignPlants: { components: [ForeignPlantComponent] },
	localPlants: {
		components: [PlantedComponent, SavableObject],
		listen: { added: true, removed: true },
	},
};
